/*
 * LUX VERSE - compact size picker.   v1  (2026-09-11, replaces lx-fit-toggle.js)
 *
 * The grid of size buttons becomes one "Select size" box that opens a list,
 * like Farfetch. Shoes labelled "40 EU | 7 US" get EU / US tabs; unisex shoes
 * ("40 EU | 7 US M / 10 US W") get EU / US Men / US Women, and the US tabs
 * list only the sizes that exist for that fit.
 *
 * The theme's own radio inputs stay in the page and do all the work: picking a
 * row clicks the matching input, so price, stock, URL and cart behave exactly
 * as before. Until the shopper picks a size, Add to cart opens the list instead
 * of adding the size the theme pre-selected.
 *
 * layout/theme.liquid hides the buttons early (no flash) and shows them again
 * after 4 s if this file never runs.
 */
(() => {
  window.__lxSize = true;
  const SIZE_NAME = /^(size|taglia|talla|taille|gr(ö|oe)(ß|ss)e)$/i;
  const store = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };

  // size system: EU or US; fit (unisex only): M or W. Both remembered.
  let sys = store.get('lx-size-sys') === 'US' ? 'US' : 'EU';
  let fit = (() => {
    const s = store.get('lx-fit');
    if (s === 'M' || s === 'W') return s;
    const hint = `${window.location.pathname} ${document.referrer}`.toLowerCase();
    return /wom[ae]n|ladies|-her\b/.test(hint) ? 'W' : 'M';
  })();

  const chosen = new Set();   // product ids where the shopper picked a size (or arrived on a ?variant= link)
  let openKey = null;         // `${productId}|${fieldsetIndex}` of the open list
  let focusKey = null;        // restore focus here after the theme re-renders
  let uid = 0;

  // "40 EU | 7 US M / 10 US W" -> { eu: '40', us: { M: '7', W: '10' } }; "40 EU | 7 US" -> us.U
  function parse(v) {
    const m = String(v).match(/^\s*(\d+(?:[.,]\d+)?)\s*EU\s*\|\s*(.+)$/i);
    if (!m) return null;
    const us = {};
    for (const part of m[2].split('/')) {
      const p = part.trim().match(/^(\d+(?:[.,]\d+)?)\s*US(?:\s*([MW]))?$/i);
      if (!p) return null;
      us[p[2] ? p[2].toUpperCase() : 'U'] = p[1];
    }
    return { eu: m[1], us };
  }

  const CSS = `
    fieldset.lx-size-on > label{display:none !important}
    fieldset.lx-size-on > legend{position:absolute !important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}
    .lx-size{text-transform:uppercase;letter-spacing:.02em;grid-column:1/-1;width:100%;font-size:14px;line-height:20px;color:#000;text-align:left}
    .lx-size button{font:inherit;color:inherit;letter-spacing:inherit;text-transform:inherit;margin:0}
    .lx-size__wrap{position:relative}
    .lx-size__box{appearance:none;-webkit-appearance:none;display:flex;align-items:center;justify-content:space-between;gap:12px;
      width:100%;min-height:50px;padding:14px 16px;background:#fff;border:0.8px solid #000;border-radius:0;cursor:pointer;text-align:left}
    .lx-size__box:focus-visible{outline:2px solid #000;outline-offset:2px}
    .lx-size__box svg{flex:none;width:12px;height:12px;transition:transform .15s ease}
    .lx-size[data-open="true"] .lx-size__box svg{transform:rotate(180deg)}
    .lx-size__placeholder{color:#000}
    .lx-size__alt{color:#6b6b6b;font-size:13px}
    .lx-size__panel{position:absolute;z-index:30;left:0;right:0;top:100%;background:#fff;border:0.8px solid #000;border-top:0;
      box-shadow:0 12px 24px rgba(0,0,0,.08)}
    .lx-size__tabs{display:flex;gap:20px;margin:0 16px;border-bottom:0.8px solid #e3e3e3}
    .lx-size__tab{appearance:none;-webkit-appearance:none;background:none;border:0;border-bottom:2px solid transparent;
      padding:12px 0 8px;cursor:pointer;color:#6b6b6b}
    .lx-size__tab[aria-selected="true"]{color:#000;border-bottom-color:#000}
    .lx-size__tab:focus-visible{outline:2px solid #000;outline-offset:2px}
    .lx-size__list{list-style:none;margin:0;padding:4px 0;max-height:min(320px,55vh);overflow-y:auto;overscroll-behavior:contain}
    .lx-size__opt{display:flex;align-items:baseline;gap:10px;padding:10px 16px;cursor:pointer;outline:none}
    .lx-size__opt:hover,.lx-size__opt:focus-visible{background:#f4f4f4}
    .lx-size__opt[aria-selected="true"]{font-weight:600}
    .lx-size__opt[aria-disabled="true"]{cursor:default;color:#9b9b9b}
    .lx-size__opt[aria-disabled="true"]:hover{background:none}
    .lx-size__opt[aria-disabled="true"] .lx-size__alt{color:#b5b5b5}
    .lx-size__num{font-weight:600}
    .lx-size__status{margin-left:auto;font-size:13px;font-weight:400;white-space:nowrap;font-variant-numeric:tabular-nums;letter-spacing:normal}
    .lx-size__price{color:#6b6b6b}
    .lx-size__error{margin:8px 0 0;font-size:13px;color:#b3261e}
    @media (prefers-reduced-motion: reduce){.lx-size__box svg{transition:none}}
  `;
  function injectCSS() {
    if (document.getElementById('lx-size-css')) return;
    const s = document.createElement('style');
    s.id = 'lx-size-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }


  // Prices per size, like the reference shop: the storefront's product JSON, read
  // once per product. The theme also prints the selected variant's JSON in the page;
  // if the two disagree (a currency the JSON endpoint does not convert), prices are
  // left out rather than shown wrong.
  const prices = new Map();   // productUrl -> Map(variantId -> cents) | 'off' | 'loading'
  function money(cents) {
    const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: cur }).format(cents / 100); }
    catch (e) { return `$${(cents / 100).toFixed(2)}`; }
  }
  function loadPrices(picker) {
    const url = picker && picker.dataset.productUrl;
    if (!url || prices.has(url)) return;
    prices.set(url, 'loading');
    fetch(`${url}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j || !Array.isArray(j.variants)) { prices.set(url, 'off'); return; }
        const map = new Map(j.variants.map((v) => [String(v.id), v.price]));
        let shown = null;
        try { shown = JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || 'null'); } catch (e) { /* ignore */ }
        if (shown && shown.id && typeof shown.price === 'number' && map.get(String(shown.id)) !== shown.price) { prices.set(url, 'off'); return; }
        prices.set(url, map);
        applyAll();
      })
      .catch(() => { prices.set(url, 'off'); });
  }


  // The picker's own wording follows the storefront language (Shopify sets
  // <html lang>). Size labels themselves are variant values and never change.
  const LANG = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
  const I18N = {
    en: { select: 'Select size', sold: 'Sold out', pick: 'Please select a size', men: 'US Men', women: 'US Women', system: 'Size system', size: 'Size' },
    de: { select: 'Größe wählen', sold: 'Ausverkauft', pick: 'Bitte wählen Sie eine Größe', men: 'US Herren', women: 'US Damen', system: 'Größensystem', size: 'Größe' },
    es: { select: 'Elegir talla', sold: 'Agotado', pick: 'Elige una talla', men: 'US Hombre', women: 'US Mujer', system: 'Sistema de tallas', size: 'Talla' },
    fr: { select: 'Choisir la taille', sold: 'Épuisé', pick: 'Choisissez une taille', men: 'US Homme', women: 'US Femme', system: 'Système de tailles', size: 'Taille' },
    it: { select: 'Scegli la taglia', sold: 'Esaurito', pick: 'Seleziona una taglia', men: 'US Uomo', women: 'US Donna', system: 'Sistema di taglie', size: 'Taglia' },
  };
  // Wording set in the theme's locale files wins, so it can be edited in
  // Translate & Adapt; anything missing falls back to the table above.
  const fromTheme = {};
  for (const [k, v] of Object.entries(window.LX_SIZE_I18N || {})) {
    if (typeof v === 'string' && v.trim() && !/translation missing/i.test(v)) fromTheme[k] = v;
  }
  const T = Object.assign({}, I18N[LANG] || I18N.en, fromTheme);

  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const CHEVRON = '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M1.5 4l4.5 4.5L10.5 4" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';

  function isSizeFieldset(fs) {
    if (!fs.classList.contains('variant-option--buttons')) return false;
    const inputs = fs.querySelectorAll(':scope > label input[type="radio"]');
    if (inputs.length < 2) return false;
    const name = inputs[0].dataset.optionName || fs.querySelector(':scope > legend')?.textContent || '';
    return SIZE_NAME.test(name.trim());
  }

  // the block that holds this picker and its Add to cart form
  function scopeOf(el) {
    for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
      if (n.querySelector('product-form-component, form[action*="/cart/add"]')) return n;
    }
    return null;
  }

  // what one row shows in the current tab; null = not in this tab
  function view(p, raw, unisex, tab) {
    if (!p) return { num: raw, unit: '', alt: '' };
    if (tab === 'EU') {
      const alt = unisex
        ? ['M', 'W'].filter((g) => p.us[g]).map((g) => `${p.us[g]} US ${g}`).join(' · ')
        : p.us.U ? `${p.us.U} US` : '';
      return { num: p.eu, unit: 'EU', alt };
    }
    const us = unisex ? p.us[tab] : p.us.U;
    return us ? { num: us, unit: 'US', alt: `${p.eu} EU` } : null;
  }
  const label = (v) => `<span class="lx-size__num">${esc(v.num)}</span>${v.unit ? ` ${v.unit}` : ''}${v.alt ? ` <span class="lx-size__alt">· ${esc(v.alt)}</span>` : ''}`;

  function tabsFor(parsed, unisex) {
    if (!parsed) return [];
    return unisex ? [['EU', 'EU'], ['M', T.men], ['W', T.women]] : [['EU', 'EU'], ['US', 'US']];
  }
  const activeTab = (unisex) => (sys === 'EU' ? 'EU' : unisex ? fit : 'US');

  function render(fs) {
    const picker = fs.closest('variant-picker');
    const pid = picker?.dataset.productId || '';
    const key = `${pid}|${fs.dataset.fieldsetIndex || 0}`;
    const inputs = [...fs.querySelectorAll(':scope > label input[type="radio"]')];
    const items = inputs.map((input) => ({ input, raw: input.value, p: parse(input.value),
      avail: input.dataset.optionAvailable !== 'false' && !input.disabled, checked: input.checked }));
    const parsed = items.every((x) => x.p);
    const unisex = parsed && items.some((x) => x.p.us.M || x.p.us.W);
    const tab = parsed ? activeTab(unisex) : 'EU';
    const isChosen = chosen.has(pid);
    const open = openKey === key;

    loadPrices(picker);
    const scope = scopeOf(fs);
    if (scope) {
      const need = !isChosen && items.some((x) => x.avail);
      if (scope.hasAttribute('data-lx-need-size') !== need) scope.toggleAttribute('data-lx-need-size', need);
    }

    if (!fs.classList.contains('lx-size-on')) fs.classList.add('lx-size-on');
    const priceState = typeof prices.get(picker?.dataset.productUrl || '') === 'object' ? 'p' : '-';
    const sig = [tab, isChosen, open, priceState, ...items.map((x) => `${x.raw}:${x.avail}:${x.checked}`)].join('|');
    let root = fs.querySelector(':scope > .lx-size');
    if (root && root.dataset.sig === sig) return;

    const id = root?.dataset.uid || `lxs${++uid}`;
    const sel = items.find((x) => x.checked);
    let boxHtml = `<span class="lx-size__placeholder">${esc(T.select)}</span>`;
    if (isChosen && sel) boxHtml = `<span>${label(view(sel.p, sel.raw, unisex, tab) || view(sel.p, sel.raw, unisex, 'EU'))}</span>`;

    const tabs = tabsFor(parsed, unisex);
    const pmap = prices.get(picker?.dataset.productUrl || '');
    const priceOf = (x) => {
      if (!pmap || typeof pmap === 'string') return '';
      const c = pmap.get(String(x.input.dataset.variantId || ''));
      return typeof c === 'number' ? money(c) : '';
    };
    const rows = items.map((x, i) => {
      const v = view(x.p, x.raw, unisex, tab);
      if (!v) return '';
      const selected = isChosen && x.checked;
      return `<li class="lx-size__opt" role="option" id="${id}-o${i}" data-i="${i}" tabindex="-1" aria-selected="${selected}"${x.avail ? '' : ' aria-disabled="true"'}>`
        + `<span>${label(v)}</span><span class="lx-size__status${x.avail ? ' lx-size__price' : ''}">${x.avail ? esc(priceOf(x)) : esc(T.sold)}</span></li>`;
    }).join('');

    const html = `<div class="lx-size__wrap"><button type="button" class="lx-size__box" aria-haspopup="listbox" aria-expanded="${open}" aria-controls="${id}-list">${boxHtml}${CHEVRON}</button>`
      + `<div class="lx-size__panel"${open ? '' : ' hidden'}>`
      + (tabs.length ? `<div class="lx-size__tabs" role="tablist" aria-label="${esc(T.system)}">${tabs.map(([t, txt]) => `<button type="button" class="lx-size__tab" role="tab" data-tab="${t}" aria-selected="${t === tab}">${txt}</button>`).join('')}</div>` : '')
      + `<ul class="lx-size__list" id="${id}-list" role="listbox" aria-label="${esc(T.size)}">${rows}</ul></div></div>`
      + `<p class="lx-size__error" role="alert" hidden>${esc(T.pick)}</p>`;

    const hadError = root && !root.querySelector('.lx-size__error')?.hidden;
    const listScroll = root?.querySelector('.lx-size__list')?.scrollTop || 0;
    if (!root) {
      root = document.createElement('div');
      root.className = 'lx-size';
      root.dataset.uid = id;
      root.dataset.key = key;
      fs.appendChild(root);
    }
    root.innerHTML = html;
    root.dataset.open = String(open);
    root.dataset.sig = sig;
    if (hadError && !isChosen) root.querySelector('.lx-size__error').hidden = false;
    const list = root.querySelector('.lx-size__list');
    list.scrollTop = listScroll;
    if (open && !listScroll) root.querySelector('.lx-size__opt[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
    if (focusKey === key) {
      focusKey = null;
      (open ? list.querySelector('.lx-size__opt[aria-selected="true"], .lx-size__opt:not([aria-disabled])') : root.querySelector('.lx-size__box'))?.focus();
    }
  }

  let busy = false;
  function applyAll() {
    if (busy) return;
    busy = true;
    try {
      injectCSS();
      document.querySelectorAll('variant-picker fieldset').forEach((fs) => {
        if (isSizeFieldset(fs)) render(fs);
        else if (!fs.classList.contains('lx-size-skip')) fs.classList.add('lx-size-skip');
      });
    } finally {
      busy = false;
    }
  }

  const rootOf = (el) => el?.closest?.('.lx-size');
  const fsOf = (root) => root.closest('fieldset');

  function setOpen(root, open, focus) {
    const key = root.dataset.key;
    openKey = open ? key : openKey === key ? null : openKey;
    if (focus) focusKey = key;
    applyAll();
  }

  function choose(root, li) {
    if (li.getAttribute('aria-disabled') === 'true') return;
    const fs = fsOf(root);
    const input = fs.querySelectorAll(':scope > label input[type="radio"]')[Number(li.dataset.i)];
    const pid = fs.closest('variant-picker')?.dataset.productId || '';
    chosen.add(pid);
    openKey = null;
    focusKey = root.dataset.key;
    if (!input.checked) input.click();   // the theme takes it from here (price, stock, URL, cart)
    applyAll();
  }

  function prompt(scope) {
    const fs = [...scope.querySelectorAll('fieldset.lx-size-on')].find((f) => f.querySelector(':scope > .lx-size'));
    const root = fs?.querySelector(':scope > .lx-size');
    if (!root) return;
    openKey = root.dataset.key;
    focusKey = root.dataset.key;
    applyAll();
    const r = fs.querySelector(':scope > .lx-size');
    r.querySelector('.lx-size__error').hidden = false;
    const box = r.getBoundingClientRect();
    if (box.top < 0 || box.bottom > window.innerHeight) r.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  // ---- events (one set of listeners for the whole page)
  document.addEventListener('click', (e) => {
    const root = rootOf(e.target);
    if (!root) return;
    const box = e.target.closest('.lx-size__box');
    const tabBtn = e.target.closest('.lx-size__tab');
    const li = e.target.closest('.lx-size__opt');
    if (box || tabBtn || li) { e.preventDefault(); e.stopPropagation(); }
    if (box) setOpen(root, root.dataset.open !== 'true', false);
    else if (tabBtn) {
      const t = tabBtn.dataset.tab;
      if (t === 'EU') sys = 'EU';
      else { sys = 'US'; if (t === 'M' || t === 'W') { fit = t; store.set('lx-fit', fit); } }
      store.set('lx-size-sys', sys);
      focusKey = null;
      applyAll();
      rootOf(document.querySelector(`.lx-size[data-key="${root.dataset.key}"]`))?.querySelector(`.lx-size__tab[data-tab="${t}"]`)?.focus();
    } else if (li) choose(root, li);
  });

  // close when clicking anywhere else
  document.addEventListener('pointerdown', (e) => {
    if (!openKey) return;
    const root = rootOf(e.target);
    if (root && root.dataset.key === openKey) return;
    openKey = null;
    applyAll();
  });

  document.addEventListener('keydown', (e) => {
    const root = rootOf(e.target);
    if (!root) return;
    const open = root.dataset.open === 'true';
    if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(root, false, true); return; }
    if (e.target.closest('.lx-size__box') && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && !open) {
      e.preventDefault(); setOpen(root, true, true); return;
    }
    const opts = [...root.querySelectorAll('.lx-size__opt:not([aria-disabled="true"])')];
    const li = e.target.closest('.lx-size__opt');
    if (li) {
      const i = opts.indexOf(li);
      if (e.key === 'ArrowDown') { e.preventDefault(); (opts[i + 1] || opts[i])?.focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (opts[i - 1] || root.querySelector('.lx-size__tab[aria-selected="true"]') || li).focus(); }
      else if (e.key === 'Home') { e.preventDefault(); opts[0]?.focus(); }
      else if (e.key === 'End') { e.preventDefault(); opts[opts.length - 1]?.focus(); }
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(root, li); }
    }
    const tabBtn = e.target.closest('.lx-size__tab');
    if (tabBtn && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
      e.preventDefault();
      const tabs = [...root.querySelectorAll('.lx-size__tab')];
      const j = (tabs.indexOf(tabBtn) + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      tabs[j].click();
    } else if (tabBtn && e.key === 'ArrowDown') { e.preventDefault(); opts.find((o) => o.getAttribute('aria-selected') === 'true')?.focus() || opts[0]?.focus(); }
  });

  // leaving the component with Tab closes it
  document.addEventListener('focusout', (e) => {
    const root = rootOf(e.target);
    if (!root || root.dataset.open !== 'true') return;
    setTimeout(() => {
      if (openKey !== root.dataset.key) return;
      const now = document.querySelector(`.lx-size[data-key="${root.dataset.key}"]`);
      if (now && !now.contains(document.activeElement) && document.activeElement !== document.body) { openKey = null; applyAll(); }
    }, 0);
  });

  // Add to cart before a size was picked: open the list instead. Window capture
  // runs before the theme's own click handling.
  window.addEventListener('click', (e) => {
    const btn = e.target.closest?.('button[name="add"], add-to-cart-component button, [type="submit"][form]');
    const scope = btn?.closest('[data-lx-need-size]');
    if (!scope) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    prompt(scope);
  }, true);
  window.addEventListener('submit', (e) => {
    const scope = e.target.matches?.('form[action*="/cart/add"]') && e.target.closest('[data-lx-need-size]');
    if (!scope) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    prompt(scope);
  }, true);

  // The theme re-renders the picker on every option change; MutationObserver
  // callbacks run before the next paint, so the buttons never flash back.
  // render() only writes when something differs, so this cannot loop.
  const mo = new MutationObserver(() => applyAll());
  function start() {
    if (/[?&]variant=\d/.test(window.location.search)) {
      document.querySelectorAll('variant-picker[data-template-product-match="true"]').forEach((p) => chosen.add(p.dataset.productId || ''));
    }
    applyAll();
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
